// Sugerencia de corrección para el DOMINIO de un email mal tecleado.
//
// Se usa en la pantalla de login cuando el backend contesta ONLY_BUYERS: ese error ya
// nos dice que con ese mail no hay ninguna compra, así que una sugerencia equivocada no
// cuesta nada (el usuario la ignora y sigue). Por eso el umbral es generoso: preferimos
// ofrecer de más antes que dejar al comprador golpeando la puerta.
//
// Casos reales medidos en audit_login_otp / stripe_payments (jul-2026):
//   gmail.vom · gmail.cin · gmail.coma · gmual.com · gmalil.com · gmil.com · hmail.com
//   viriglio.it (virgilio.it) · orange.frj · "@gmail" sin TLD · mail.com (gmail.com)
//
// Solo toca el dominio. La parte local no se sugiere nunca: ahí no hay lista de
// referencia y una sugerencia inventada confunde más de lo que ayuda.

// Dominios de referencia, ordenados por volumen en la base de compradores.
const KNOWN_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "outlook.com",
  "yahoo.com",
  "icloud.com",
  "live.com",
  "me.com",
  "msn.com",
  "aol.com",
  "protonmail.com",
  "proton.me",
  "gmx.com",
  "gmx.de",
  "web.de",
  "t-online.de",
  "freenet.de",
  "yahoo.es",
  "hotmail.es",
  "outlook.es",
  "yahoo.com.ar",
  "hotmail.com.ar",
  "hotmail.com.br",
  "yahoo.com.br",
  "uol.com.br",
  "bol.com.br",
  "terra.com.br",
  "globo.com",
  "yahoo.fr",
  "hotmail.fr",
  "outlook.fr",
  "orange.fr",
  "wanadoo.fr",
  "free.fr",
  "sfr.fr",
  "laposte.net",
  "libero.it",
  "virgilio.it",
  "alice.it",
  "tiscali.it",
  "yahoo.it",
  "hotmail.it",
  "tin.it",
  "yahoo.co.uk",
  "hotmail.co.uk",
  "btinternet.com",
  "yahoo.co.jp",
  "docomo.ne.jp",
  "ezweb.ne.jp",
  "softbank.ne.jp",
  "naver.com",
  "qq.com",
  "163.com",
  "telefonica.net",
  "movistar.es",
  "prodigy.net.mx",
  "googlemail.com",
  "zoho.com",
  "mail.ru",
  "yandex.com",
];

/**
 * Distancia de Levenshtein con corte: si supera `max`, devuelve max + 1 y no sigue.
 * El corte importa porque esto corre en cada submit contra ~60 dominios.
 */
function distance(a, b, max) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;

  let prev = new Array(b.length + 1);
  let curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    let best = curr[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (curr[j] < best) best = curr[j];
    }
    if (best > max) return max + 1;
    const tmp = prev;
    prev = curr;
    curr = tmp;
  }
  return prev[b.length];
}

/**
 * Sugiere una corrección del dominio, o null si no hay nada claro que ofrecer.
 * Devuelve el email completo ya corregido (misma parte local).
 *
 * Reglas, en orden:
 *  1. Dominio conocido => no se sugiere nada.
 *  2. Dominio sin punto ("@gmail") => se completa si hay UN solo conocido que empiece así.
 *  3. Distancia 1 contra un conocido => se sugiere.
 *  4. Distancia 2 contra un conocido, solo si el dominio tiene 8+ caracteres (en dominios
 *     cortos dos ediciones ya son otro dominio, no un error de tipeo).
 *  5. Empate entre dos conocidos a la misma distancia => no se sugiere (ambiguo).
 */
export function suggestEmail(rawEmail) {
  const email = String(rawEmail || "").trim().toLowerCase();
  const at = email.lastIndexOf("@");
  if (at <= 0 || at === email.length - 1) return null;

  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (!local || !domain) return null;
  if (KNOWN_DOMAINS.includes(domain)) return null;

  // 2) Sin TLD: "@gmail", "@hotmail". Solo si un único conocido arranca con ese prefijo.
  if (!domain.includes(".")) {
    const starts = KNOWN_DOMAINS.filter((d) => d.startsWith(domain + "."));
    return starts.length === 1 ? `${local}@${starts[0]}` : null;
  }

  const maxDist = domain.length >= 8 ? 2 : 1;
  let best = null;
  let bestDist = maxDist + 1;
  let tie = false;

  for (const known of KNOWN_DOMAINS) {
    const d = distance(domain, known, maxDist);
    if (d > maxDist) continue;
    if (d < bestDist) {
      bestDist = d;
      best = known;
      tie = false;
    } else if (d === bestDist && known !== best) {
      tie = true;
    }
  }

  if (!best || tie) return null;
  return `${local}@${best}`;
}

export default suggestEmail;
